import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../redux/slice";
import { selectForm, selectIsAddModalOpen, selectLoading } from "../../redux/selector";
import { supplierFormState } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof supplierFormState;
  label: string;
  type: "select" | "text";
};

const FIELDS: FieldConfig[] = [
  { key: "vendorName",  label: "VENDOR_NAME",  type: "text" },
  { key: "telephone",   label: "TELEPHONE",    type: "text" },
  { key: "fax",         label: "FAX",          type: "text" },
  { key: "contact",     label: "CONTACT",      type: "text" },
  { key: "address",     label: "ADDRESS",      type: "text" },
  { key: "phoneOffice", label: "PHONE_OFFICE", type: "text" },
  { key: "headline",    label: "HEADLINE",     type: "text" },
];

const REQUIRED_FIELDS: (keyof supplierFormState)[] = ["vendorName", "telephone", "contact", "address"];

const P = "SUPPLIERS.MODAL";

const AddSupplierModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();

  const form = useSelector(selectForm);
  const isLoading = useSelector(selectLoading);
  const isOpen = useSelector(selectIsAddModalOpen);

  const onClose = () => {
    if (isLoading) return;
    dispatch(actions.resetForm());
    dispatch(actions.closeAddModal());
  };

  const placeholder = (label: string, type: "select" | "text") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const isValid = REQUIRED_FIELDS.every((f) => Boolean(String(form[f] ?? "").trim()));

  const onSubmit = () => {
    dispatch(actions.craeteSupplier(form));
    dispatch(actions.closeAddModal());
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            disabled={!!isLoading}
            aria-label={t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label, type }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${P}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                select={type === "select"}
                variant="outlined"
                placeholder={placeholder(label, type)}
                value={String(form[key] ?? "")}
                disabled={!!isLoading}
                onChange={(e) =>
                  dispatch(actions.setFormField({ [key]: e.target.value }))
                }
                InputProps={{ notched: false }}
                SelectProps={
                  type === "select"
                    ? {
                        displayEmpty: true,
                        IconComponent: DropdownIcon,
                        renderValue: (value) =>
                          (value as string) || placeholder(label, "select"),
                      }
                    : undefined
                }
              >
                {type === "select" ? (
                  <MenuItem value="">{placeholder(label, "select")}</MenuItem>
                ) : undefined}
              </TextField>
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            disabled={!!isLoading}
            onClick={onClose}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || !!isLoading}
            onClick={onSubmit}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddSupplierModal;
